"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { LISTING_TYPES } from "@/lib/constants";

export function CreateAlertDialog() {
    const { data: session } = useSession();
    const searchParams = useSearchParams();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Initial values from URL or defaults
    const [query, setQuery] = useState(searchParams.get("q") ?? "");
    const [postalCode, setPostalCode] = useState("");
    const [radius, setRadius] = useState(50);
    const [type, setType] = useState<string>(searchParams.get("type") ?? "ALL");
    const categoryId = searchParams.get("categorie");

    const handleCreate = async () => {
        if (!session) {
            toast.error("Connectez-vous pour créer une alerte");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch("/api/alerts", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    query: query || undefined,
                    postalCode: postalCode || undefined,
                    radius,
                    type: type === "ALL" ? undefined : type,
                    categoryId: categoryId || undefined,
                }),
            });

            if (!res.ok) throw new Error("Erreur lors de la création");

            toast.success("Alerte créée avec succès !");
            setOpen(false);
        } catch (error) {
            toast.error("Impossible de créer l'alerte");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-hp-teal text-hp-teal hover:bg-hp-teal/10">
                    <Bell className="h-4 w-4" />
                    Créer une alerte
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>M'alerter des nouvelles annonces</DialogTitle>
                    <DialogDescription>
                        Recevez une notification quand une annonce correspondant à ces critères est publiée.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label htmlFor="query">Mots-clés</Label>
                        <Input
                            id="query"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Ex: Fauteuil roulant..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="location">Code postal</Label>
                            <Input
                                id="location"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                placeholder="75000"
                                maxLength={5}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="type">Type</Label>
                            <Select value={type} onValueChange={setType}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Tout type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ALL">Tout type</SelectItem>
                                    {Object.entries(LISTING_TYPES).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>
                                            {config.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label>Rayon : {radius} km</Label>
                        <Slider
                            value={[radius]}
                            onValueChange={(val: number[]) => setRadius(val[0])}
                            min={5}
                            max={200}
                            step={5}
                            className="py-4"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                    <Button onClick={handleCreate} disabled={loading} className="bg-hp-teal hover:bg-hp-teal-dark text-white">
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Créer l'alerte
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
